/** @jsx jsx */
import React, { useRef, useState } from 'react';
import { jsx } from '@emotion/core';
import Select, { OptionType } from 'components/atoms/Select';

/** 共通の Select の一覧の 1 項目の高さ（px）と、一覧の上下の余白・最大の高さ（react-select の既定） */
const OPTION_HEIGHT = 36;
const MENU_PADDING = 8;
const MENU_MAX_HEIGHT = 300;

/**
 * シフト編成（新）のプルダウン（10/7）
 *
 * ほかの画面（勤務スケジュールの「シフト追加」など）と同じ、アプリ共通の Select（react-select）を使う。
 * 以前はブラウザ標準の <select> で、一覧の見た目がほかの画面と違っていた。
 * 共通の Select には手を入れない（ほかの画面に影響させない）ので、ここで次を受け持つ。
 * - 値は文字列で受け渡す（数値の項目は呼び出し側で変換する）
 * - 共通の Select は id や aria-label を受け取れないので、外枠に付ける（ラベルと e2e の目印）
 * - 一覧を開く向き。共通の Select の一覧はダイアログの本文（スクロールする枠）の中に出るので、本文の下の方の欄では
 *   一覧が枠で切れる。開くときに枠の中の上下の余裕を測り、共通の Select の menuPlacement（下 / 上）で向きを決める。
 *   どちらにも入らない長い一覧は、下に開いて枠をスクロールする
 */
const BoardSelect: React.FC<{
  /** 外枠の id。共通の Select の name にも使う */
  id: string;
  /** 外枠の aria-label（見出しの文字が近くに無いとき） */
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<OptionType>;
  disabled?: boolean;
  /** 値が選択肢に無いときの表示。指定しなければ共通の Select の既定（選択してください） */
  placeholder?: string;
  /** 外枠の見た目（幅・flex など） */
  style?: React.CSSProperties;
}> = ({
  id, label, value, onChange, options, disabled = false, placeholder, style,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<'bottom' | 'top'>('bottom');

  /** 一番近いスクロールする枠（ダイアログの本文など）。無ければ null（画面） */
  const frameOf = (el: HTMLElement): HTMLElement | null => {
    let frame: HTMLElement | null = el.parentElement;
    while (frame && !['auto', 'scroll'].includes(getComputedStyle(frame).overflowY)) {
      frame = frame.parentElement;
    }
    return frame;
  };

  /** 開いた一覧が枠の下で切れていたら、枠をスクロールして一覧を見せる */
  const revealMenu = () => {
    const el = ref.current;
    const container = el ? el.firstElementChild : null;
    // 共通の Select（react-select）の中の DIV は「入力欄 → 一覧」の順。一覧は開いているときだけある
    // （ほかに読み上げ用の SPAN と値の hidden の INPUT がある）
    const divs = container ? Array.from(container.children).filter((c) => c.tagName === 'DIV') : [];
    const menu = divs.length > 1 ? divs[1] as HTMLElement : null;
    const frame = el ? frameOf(el) : null;
    if (!menu || !frame) {
      return;
    }
    const frameBottom = Math.min(frame.getBoundingClientRect().bottom, window.innerHeight);
    const overflow = menu.getBoundingClientRect().bottom - frameBottom + MENU_PADDING;
    if (overflow > 0) {
      frame.scrollTop += overflow;
    }
  };

  /**
   * 開く向きを決める。枠（無ければ画面）の中で、下に入れば下、上に入れば上。
   * どちらにも入らないときは下に開き、開いたあとで枠をスクロールして一覧を見せる（上に開くと、上にはスクロールできないので切れる）
   */
  const decidePlacement = () => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const frame = frameOf(el);
    const frameRect = frame ? frame.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
    const rect = el.getBoundingClientRect();
    const menuHeight = Math.min(MENU_MAX_HEIGHT, options.length * OPTION_HEIGHT + MENU_PADDING);
    const below = Math.min(frameRect.bottom, window.innerHeight) - rect.bottom;
    const above = rect.top - Math.max(frameRect.top, 0);
    if (below >= menuHeight) {
      setPlacement('bottom');
    } else if (above >= menuHeight) {
      setPlacement('top');
    } else {
      setPlacement('bottom');
      window.setTimeout(revealMenu, 50);
    }
  };

  return (
    <div
      ref={ref}
      id={id}
      role="group"
      aria-label={label}
      style={{ minWidth: 0, ...style }}
      onMouseDownCapture={decidePlacement}
      onFocusCapture={decidePlacement}
    >
      <Select
        name={id}
        value={value}
        setValue={onChange}
        options={options}
        isDisabled={disabled}
        placeholder={placeholder}
        width="100%"
        menuPlacement={placement}
      />
    </div>
  );
};

export default BoardSelect;
